import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, UserPlus } from "lucide-react";

interface Option {
  id: number;
  label: string;
}

interface Props {
  options: Option[];
  onConfirm: (ids: number[]) => Promise<unknown>;
  placeholder?: string;
  confirmLabel?: (count: number) => string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
}

/** Inline multi-select checkbox dropdown for "assign several people at once" flows —
 * check as many options as needed, then confirm once, instead of a single-select +
 * Add loop repeated per person. Confirming calls onConfirm with every checked id;
 * callers that only have a single-id assign API should loop over the ids themselves. */
export default function MultiAssignDropdown({
  options,
  onConfirm,
  placeholder = "Select employees…",
  confirmLabel = (n) => (n > 0 ? `Add ${n}` : "Add"),
  emptyMessage = "No eligible employees.",
  disabled,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  // Selections reference option ids from the caller's `options` prop, which shrinks
  // as people get assigned elsewhere — drop any id that's no longer selectable.
  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(options.map((o) => o.id));
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [options]);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirm() {
    if (selected.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(Array.from(selected));
      setSelected(new Set());
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Failed to assign. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || options.length === 0}
        className="flex items-center justify-between gap-2 w-full min-w-[220px] bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed hover:border-muted-foreground transition-colors"
      >
        <span className="truncate">{selected.size > 0 ? `${selected.size} selected` : placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[220px] bg-surface-muted border border-border rounded-xl shadow-2xl overflow-hidden">
          <div className="max-h-56 overflow-y-auto py-1">
            {options.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground font-inter">{emptyMessage}</p>
            ) : (
              options.map((opt) => {
                const checked = selected.has(opt.id);
                return (
                  <label
                    key={opt.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground font-inter hover:bg-surface cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={checked}
                      onChange={() => toggle(opt.id)}
                    />
                    <span
                      className={`flex items-center justify-center w-4 h-4 rounded border shrink-0 ${
                        checked ? "bg-primary border-primary" : "border-border"
                      }`}
                    >
                      {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>
          {options.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
              {error && <span className="text-[11px] text-destructive font-inter">{error}</span>}
              <button
                type="button"
                onClick={confirm}
                disabled={saving || selected.size === 0}
                className="flex items-center gap-1.5 ml-auto px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {saving ? "Adding…" : confirmLabel(selected.size)}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
